// ─────────────────────────────────────────
//  MAIN ENTRY POINT
//  Imports all modules and wires them to the DOM
// ─────────────────────────────────────────

// Core modules
import { S, sb, ls, setLs, rmLs } from './state.js';
import {
  esc, extractCode, sleep, parsePlanSteps, detectCategory,
  showToast, simpleHash, trackSignal, autoResize, toggleVoice,
  initDragScroll, initAutoScroll
} from './utils.js';
import { MODELS, aiRequest, aiRequestStream, aiRequestSilent, checkPendingBuild } from './ai.js';
import { applyCode, onCodeChange, updateStats, notifyCodeUpdate, clearChat } from './editor.js';
import { runCode, runCodeSilent, openFullscreen } from './preview.js';
import {
  openSave, closeSave, saveProject, autoSaveProject, getProjects,
  renderProjects, loadProject, delProject, moveProject,
  dragProject, dragOverProject, dropProject, dragEndProject,
  shareProject, openDrawer, closeDrawer,
  confirmDelProject, closeDelConfirm, onDelConfirmInput, executeDelete
} from './projects.js';
import {
  shareCode, deployProject, copyDeployUrl, shareDeployUrl,
  closeDeployModal, downloadCode, downloadFullStackZip, pushToGitHub,
  initPublishUI, publishToSubdomain
} from './deploy.js';
import {
  addMsg, showTyping, removeTyping, createStreamBubble, renderStreamText,
  addApplyButtons, showSteps, updateStep, hideSteps, updateStatusPill,
  goView, setStatus, showStatus
} from './ui.js';
import {
  switchAuthTab, showAuthError, authSignUp, authLogIn,
  authGoogle, authResetPassword, authSignOut, onAuthSuccess
} from './auth.js';
import { loadActiveConversation, saveConversation, clearConversation } from './conversations.js';
import { buildSystem, preloadBestExamples, getBestExample } from './prompts.js';
import { callAI, isComplexRequest, isModificationRequest, simpleBuild, complexBuild } from './agents.js';
import { filterTemplates, useTemplate, useFullStackTemplate, hideGallery, loadRemix } from './templates.js';
import {
  fullStackBuild, handleFullStackEvent, generateFullStackPreview,
  showFullStackResult, showFullStackFiles, toggleFullStackMode,
  showFullStackButton, isProPlus, showProPlusUpgrade, checkCloudSyncNeeded,
  openUploadFolder, closeUploadFolder, handleFolderUpload,
  previewUploadedProject, pushUploadedToGitHub, downloadUploadedZip
} from './fullstack.js';
import {
  openSettings, closeSettings, saveSettings, showSub,
  subscribePlan, closeProPlus, subscribeProPlus, subscribeProPlusAnnual,
  backToPlans, closeSub, redeemPromo, exitPlayground,
  connectGitHub, connectSupabase
} from './settings.js';

// ─────────────────────────────────────────
//  EXPOSE TO GLOBAL SCOPE
//  (needed for onclick handlers in HTML)
// ─────────────────────────────────────────
const W = window;

// Auth
W.switchAuthTab = switchAuthTab;
W.authSignUp = authSignUp;
W.authLogIn = authLogIn;
W.authGoogle = authGoogle;
W.authResetPassword = authResetPassword;
W.authSignOut = authSignOut;

// Editor
W.clearChat = clearChat;
W.onCodeChange = onCodeChange;
W.applyCode = applyCode;

// Preview
W.runCode = runCode;
W.openFullscreen = openFullscreen;

// Projects
W.openSave = openSave;
W.closeSave = closeSave;
W.saveProject = saveProject;
W.openDrawer = openDrawer;
W.closeDrawer = closeDrawer;
W.loadProject = loadProject;
W.delProject = delProject;
W.moveProject = moveProject;
W.dragProject = dragProject;
W.dragOverProject = dragOverProject;
W.dropProject = dropProject;
W.dragEndProject = dragEndProject;
W.shareProject = shareProject;
W.confirmDelProject = confirmDelProject;
W.closeDelConfirm = closeDelConfirm;
W.onDelConfirmInput = onDelConfirmInput;
W.executeDelete = executeDelete;

// Deploy
W.shareCode = shareCode;
W.deployProject = deployProject;
W.copyDeployUrl = copyDeployUrl;
W.shareDeployUrl = shareDeployUrl;
W.closeDeployModal = closeDeployModal;
W.downloadCode = downloadCode;
W.downloadFullStackZip = downloadFullStackZip;
W.pushToGitHub = pushToGitHub;
W.publishToSubdomain = publishToSubdomain;

// Utils
W.toggleVoice = toggleVoice;
W.autoResize = autoResize;

// UI / Navigation
W.goView = goView;

// Templates
W.filterTemplates = filterTemplates;
W.useTemplate = useTemplate;
W.useFullStackTemplate = useFullStackTemplate;
W.hideGallery = hideGallery;
W.loadRemix = loadRemix;

// Agents
W.callAI = callAI;

// Full-Stack
W.toggleFullStackMode = toggleFullStackMode;
W.showFullStackFiles = showFullStackFiles;
W.showProPlusUpgrade = showProPlusUpgrade;

// Folder Upload (Pro+)
W.openUploadFolder = openUploadFolder;
W.closeUploadFolder = closeUploadFolder;
W.handleFolderUpload = handleFolderUpload;
W.previewUploadedProject = previewUploadedProject;
W.pushUploadedToGitHub = pushUploadedToGitHub;
W.downloadUploadedZip = downloadUploadedZip;

// Settings & Subscription
W.openSettings = openSettings;
W.closeSettings = closeSettings;
W.saveSettings = saveSettings;
W.exitPlayground = exitPlayground;
W.showSub = showSub;
W.subscribePlan = subscribePlan;
W.closeProPlus = closeProPlus;
W.subscribeProPlus = subscribeProPlus;
W.subscribeProPlusAnnual = subscribeProPlusAnnual;
W.backToPlans = backToPlans;
W.closeSub = closeSub;
W.redeemPromo = redeemPromo;
W.connectGitHub = connectGitHub;
W.connectSupabase = connectSupabase;

// Conversations
W.clearConversation = clearConversation;

// ─────────────────────────────────────────
//  CHAT FUNCTIONS (send, handleKey, qp)
//  These live here because they orchestrate
//  across multiple modules
// ─────────────────────────────────────────
function handleKey(e) {
  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
}

function qp(text) {
  document.getElementById('chat-input').value = text;
  send();
}

async function send() {
  const inp = document.getElementById('chat-input');
  const txt = inp?.value.trim() || '';
  if (!txt || S.busy) return;

  // Must be logged in
  if (!S.session) {
    document.getElementById('auth-screen')?.classList.remove('hidden');
    showToast('Please sign in first');
    return;
  }

  // Paywall check
  if (!S.isAdmin) {
    const limits = { free: 5, member: 20, pro: 99999, pro_annual: 99999, pro_builder: 99999, cohort: 50, byok: 99999, starter: 25, pro_plus: 99999, annual: 99999, builder: 99999 };
    const limit = limits[S.tier] ?? 5;
    if (S.builds >= limit && !S.key) {
      showSub(); return;
    }
  }

  if (inp) { inp.value = ''; inp.style.height = 'auto'; }
  hideGallery();

  // Only clear code/preview when there's nothing built yet (first build)
  // If user has existing code, KEEP IT — the AI will modify it
  if (!S.code) {
    const editor = document.getElementById('code-editor');
    if (editor) editor.value = '';
    const iframe = document.getElementById('preview-iframe');
    if (iframe) iframe.srcdoc = '';
    const urlEl = document.getElementById('preview-url');
    if (urlEl) urlEl.textContent = 'Building...';
    updateStats();
  }

  addMsg('user', txt);
  S.history.push({ role: 'user', content: txt });
  await callAI();
}

function switchMode(m, el) {
  S.mode = m;
  document.querySelectorAll('.code-tab').forEach(t => t.classList.remove('active'));
  if (el) el.classList.add('active');
  const modeStat = document.getElementById('mode-stat');
  if (modeStat) modeStat.textContent = m.toUpperCase();
}

W.handleKey = handleKey;
W.qp = qp;
W.send = send;
W.switchMode = switchMode;

// Mobile menu toggle
function toggleMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  if (menu) menu.classList.toggle('open');
}
function closeMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  if (menu) menu.classList.remove('open');
}
W.toggleMobileMenu = toggleMobileMenu;
W.closeMobileMenu = closeMobileMenu;

// Close mobile menu when tapping outside
document.addEventListener('click', (e) => {
  const menu = document.getElementById('mobile-menu');
  const btn = document.getElementById('hamburger-btn');
  if (menu && menu.classList.contains('open') && !menu.contains(e.target) && e.target !== btn) {
    menu.classList.remove('open');
  }
});

// ─────────────────────────────────────────
//  BOOT SEQUENCE
// ─────────────────────────────────────────
window.addEventListener('DOMContentLoaded', () => {
  S.key = ls('vc_key') || '';

  // Register service worker for PWA
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }

  // Listen for app resume (phone screen on, tab refocused) — check for pending builds
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkPendingBuild();
    }
  });

  // Enable drag-to-scroll on horizontal pill containers
  document.querySelectorAll('.tg-categories, #quick-prompts').forEach(initDragScroll);

  // Auto-scroll pill containers
  document.querySelectorAll('.tg-categories, #quick-prompts').forEach(initAutoScroll);

  // Folder upload drag-and-drop
  const dropZone = document.getElementById('upload-drop-zone');
  if (dropZone) {
    dropZone.addEventListener('dragover', (e) => { e.preventDefault(); dropZone.classList.add('dragover'); });
    dropZone.addEventListener('dragleave', () => { dropZone.classList.remove('dragover'); });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('dragover');
      // Handle dropped files
      if (e.dataTransfer.items) {
        for (const item of e.dataTransfer.items) {
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry?.();
            if (entry?.isDirectory) {
              // Read directory recursively
              window._readDirectoryEntry(entry, '').then(fileList => {
                handleFolderUpload(fileList);
              });
              return;
            }
          }
        }
        // Fallback: treat as flat file list
        if (e.dataTransfer.files.length) {
          handleFolderUpload(e.dataTransfer.files);
        }
      }
    });
  }

  // Gallery toggle styling
  const galleryCheckbox = document.getElementById('deploy-gallery');
  if (galleryCheckbox) {
    galleryCheckbox.addEventListener('change', function() {
      const track = document.getElementById('gallery-toggle-track');
      const thumb = document.getElementById('gallery-toggle-thumb');
      if (this.checked) {
        if (track) track.style.background = 'var(--orange)';
        if (thumb) { thumb.style.left = '22px'; thumb.style.background = '#fff'; }
      } else {
        if (track) track.style.background = 'var(--border2)';
        if (thumb) { thumb.style.left = '2px'; thumb.style.background = 'var(--muted)'; }
      }
    });
  }

  // Initialize publish-to-subdomain UI
  initPublishUI();

  // Check for remix URL parameter
  // Check for remix URL parameter
  const params = new URLSearchParams(window.location.search);
  const remixSlug = params.get('remix');
  if (remixSlug) {
    loadRemix(remixSlug);
  }

  // Check for build param from Studio (refined prompt ready to build)
  const buildPrompt = params.get('build');
  if (buildPrompt) {
    let decoded;
    try {
      decoded = decodeURIComponent(buildPrompt);
    } catch (e) {
      // If decodeURIComponent fails (malformed URI), use the raw param
      decoded = buildPrompt;
    }

    // Auto-save current work before clearing (if any exists)
    if (S.code && S.code.trim().length > 50) {
      autoSaveProject();
    }

    // Clear the workspace for the new build
    S.code = '';
    S.history = [];
    S.localProjectId = null;
    S.projectId = null;
    const editor = document.getElementById('code-editor');
    if (editor) editor.value = '';
    const iframe = document.getElementById('preview-iframe');
    if (iframe) iframe.srcdoc = '';

    // Hide template gallery and auto-send the prompt once auth is ready
    hideGallery();
    // Store the prompt so it persists through auth
    sessionStorage.setItem('kc_pending_build', decoded);
    // Wait for auth, then send
    let waitAttempts = 0;
    const waitAndBuild = () => {
      waitAttempts++;
      if (S.session) {
        sessionStorage.removeItem('kc_pending_build');
        const input = document.getElementById('chat-input');
        if (input) input.value = decoded;
        // Clean URL
        history.replaceState(null, '', '/playground.html');
        // Trigger build
        setTimeout(() => send(), 500);
      } else if (waitAttempts > 10) {
        // Auth not found after 3s — show auth screen, build will trigger after sign-in
        history.replaceState(null, '', '/playground.html');
      } else {
        setTimeout(waitAndBuild, 300);
      }
    };
    setTimeout(waitAndBuild, 500);
  }

  // If no build/remix param and no existing code, redirect to Studio
  if (!buildPrompt && !remixSlug && !S.code) {
    // Check if user has local projects (returning user with saved work)
    const hasLocalWork = localStorage.getItem('vc_projects');
    const hasProjects = hasLocalWork && JSON.parse(hasLocalWork).length > 0;
    if (!hasProjects) {
      window.location.href = '/studio';
      return;
    }
  }

  // ── Auth initialization ──
  // Two paths: 
  // 1. Existing session (cookie) → getSession() finds it immediately
  // 2. OAuth redirect (hash fragment) → onAuthStateChange fires after parsing
  
  // Listen for auth state changes (catches OAuth redirects + session refresh)
  sb.auth.onAuthStateChange((event, session) => {
    console.log('[KasiCode] Auth event:', event, !!session);
    if (session && !S.session) {
      onAuthSuccess(session);
    } else if (event === 'SIGNED_OUT') {
      S.session = null;
      S.user = null;
      S.tier = 'free';
      S.name = '';
      document.getElementById('auth-screen')?.classList.remove('hidden');
      const userPill = document.getElementById('user-pill');
      if (userPill) userPill.style.display = 'none';
    }
  });

  // Also try getSession for existing cookie-based sessions
  sb.auth.getSession()
    .then(({ data }) => {
      const session = data?.session;
      console.log('[KasiCode] getSession:', !!session);
      if (session && !S.session) {
        onAuthSuccess(session);
      }
    })
    .catch((e) => {
      console.warn('[KasiCode] getSession error:', e.message);
    });

  // Check for payment return (Yoco)
  const checkoutId = sessionStorage.getItem('yoco_checkout_id');
  if (checkoutId) {
    sessionStorage.removeItem('yoco_checkout_id');
    const planId = sessionStorage.getItem('yoco_plan_id');
    sessionStorage.removeItem('yoco_plan_id');
    // Verify payment
    fetch('/api/yoco/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkoutId, planId }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          S.tier = data.tier || planId;
          const tierEl = document.getElementById('user-pill-tier');
          if (tierEl) tierEl.textContent = S.tier.toUpperCase();
          showToast('✓ Payment successful! Plan activated.');
          document.getElementById('sub-gate')?.classList.remove('show');
        }
      })
      .catch(() => {});
  }
});

console.log('[KasiCode] All modules loaded ✓');

// ─────────────────────────────────────────
//  HELPER: Read directory entries recursively (for drag-and-drop folder upload)
// ─────────────────────────────────────────
async function readDirectoryEntry(dirEntry, path) {
  const files = [];
  const reader = dirEntry.createReader();

  function readEntries() {
    return new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
  }

  let entries = await readEntries();
  while (entries.length > 0) {
    for (const entry of entries) {
      if (entry.isFile) {
        const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
        // Attach a webkitRelativePath-like property
        Object.defineProperty(file, 'webkitRelativePath', {
          value: path ? path + '/' + entry.name : dirEntry.name + '/' + entry.name,
          writable: false,
        });
        files.push(file);
      } else if (entry.isDirectory) {
        const subPath = path ? path + '/' + entry.name : dirEntry.name + '/' + entry.name;
        const subFiles = await readDirectoryEntry(entry, subPath);
        files.push(...subFiles);
      }
    }
    entries = await readEntries();
  }

  return files;
}

// Expose for drag-and-drop handler
window._readDirectoryEntry = readDirectoryEntry;
